import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, InputAdornment, Popper, PopperPlacementType, Stack, TextField, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classnames from "classnames";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as SearchIcon } from "../../../assets/icons/search.svg";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { SubscriptionContext } from "../../../contexts/SubscriptionContext";
import { getUserEmail } from "../../../helpers/authUser";
import { createDashboardFunc, getAllUsersFunc } from "../../../lib/helper";
import { ChromeExtensionLink } from "../../../shared/dashboard";
import { Dashboard, DealOwner } from "../../../types/files";
import ArrayUtils from "../../../utils/ArrayUtils";

const useStyles = makeStyles((theme) => ({
    optionPaper: {
        background: 'white',
        border: `1px solid ${theme.colors.neutral['200']}`,
        borderRadius: 8,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
    },
    optionContainer: {
        padding: '4px 0',
        borderTop: `1px solid ${theme.colors.neutral['100']}`,
        borderBottom: `1px solid ${theme.colors.neutral['100']}`,
        "&:hover": {
            backgroundColor: '#EFF6FF',
        },
        cursor: 'pointer',
    },
    optionTitle: {
        paddingLeft: 16,
        paddingRight: 8,
        fontFamily: 'Inter',
        fontSize: '0.9rem !important',
        fontWeight: '600 !important',
        color: `${theme.colors.neutral['800']} !important`,
    },
    optionSubtitle: {
        display: '-webkit-box',
        overflow: 'hidden',
        minHeight: '0.75rem',
        paddingLeft: 16,
        paddingRight: 8,
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-word',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.85rem !important',
        fontWeight: '500 !important',
        color: `${theme.colors.neutral['600']} !important`,
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 45,
            padding: '0 8px 0 12px !important',
            borderRadius: 32,
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: `${theme.colors.neutral['400']} !important`,
            },
            "&:hover fieldset": {
                borderColor: `${theme.colors.neutral['600']} !important`,
            },
            "&.Mui-focused fieldset": {
              	borderColor: `#0d9488 !important`,
            },
        },
    },
    disabledInputField: {
        "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
                borderWidth: '1px !important',
                borderColor: 'rgba(0, 0, 0, 0.38) !important',
            },
        },
    },
    smallInputField: {
        "& .MuiOutlinedInput-root": {
            height: 32,
            borderRadius: 16,
        },
    },
    mediumInputField: {
        "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: 24,
        },
    },
    squareInputField: {
        "& .MuiOutlinedInput-root": {
            height: 45,
            borderRadius: 8,
        },
    },
    createSaveButton: {
        height: 32,
        width: 40,
        borderRadius: '16px !important',
        borderWidth: 3,
        fontSize: '14px !important',
        textTransform: 'none !important' as any,
        fontWeight: 'bold !important',
        "&:hover": {
            borderWidth: 3,
            fontWeight: 'bold !important',
        },
    },
    success: {
        background: theme.colors.success[600],
        "&:hover, &:disabled": {
            background: `${theme.colors.success[600]} !important`,
        },
        "& > svg": {
            fill: 'white !important',
        }
    },
    failed: {
        background: theme.colors.error[600],
        "&:hover, &:disabled": {
            background: `${theme.colors.error[600]} !important`,
        },
        "& > svg": {
            fill: 'white !important',
        }
    },
    smallButton: {
        height: 24,
    },
    blocked: {
        "&:hover, &:disabled": {
            cursor: 'not-allowed !important',
        },
        cursor: 'not-allowed !important',
    },
    clear: {
        minWidth: 'unset',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.warning.dark,
        textTransform: 'none',
        "&:hover": {
            background: 'unset',
        }
    },
}));

const InvalidNameNotification: React.FC<{}> = () => {
	return (<>
		<Box textAlign="center">
			<Typography component="span" fontSize="0.95rem" fontFamily="Inter">
				{'Please enter a company name.'}<br/>
				{'For DocSends, use our Chrome Extension '}
				<Typography display="inline"
					onClick={() => window.open(ChromeExtensionLink, '_blank', 'noopener, noreferrer')}
					sx={{
						cursor: 'pointer',
						color: '#fff',
						fontSize: '0.95rem',
						fontFamily: 'Inter',
						textDecoration: 'underline',
					}}>{'here'}
				</Typography>{'.'}
			</Typography>
		</Box>
	</>);
}

const ExistingNameNotification: React.FC<{}> = () => {
	return (<>
		<Box textAlign="center">
			<Typography component="span" fontSize="0.95rem" fontWeight="bold" fontFamily="Inter">
				{'Company already added'}
			</Typography>
		</Box>
	</>);
}

const ErrorNotification: React.FC<{ message: string }> = ({message}) => {
	return (<>
		<Box textAlign="center">
			<Typography component="span" fontSize="0.95rem" fontFamily="Inter">
				{message}
			</Typography>
		</Box>
	</>);
}

const DashboardsSearchOrCreate: React.FC<{
    dashboards?: Dashboard[],
    search?: boolean,
    create?: boolean,
    clear?: boolean,
    save?: boolean,
    execute?: string,
    autoComplete?: boolean,
    smallFit?: boolean,
    mediumFit?: boolean,
    squareFit?: boolean,
    disabled?: boolean,
    placement?: string,
    notify?: boolean,
    flags?: any,
    onSearch?: (searchValue: string, status: string) => void,
    onPreExecute?: () => Promise<boolean>,
    onCreate?: (dashboard: Dashboard) => void,
    onSave?: (dashboard: Dashboard) => void,
    onElseExecute?: () => void,
}> = ({ dashboards, search, create, clear, save, execute, autoComplete, smallFit, mediumFit, squareFit, disabled, placement, notify, flags,
    onSearch, onPreExecute, onCreate, onSave, onElseExecute }) => {
    const classes = useStyles();
    const { user, userGroup } = useContext(AuthContext);
    const { defaultStatus, externalSync } = useContext(GroupSettingsContext);
    const { usage, subscription } = useContext(SubscriptionContext);
    const { dashboards: globalDashboards, mappedOverviewQueries } = useContext(DashboardsContext);
    const { enqueueSnackbar } = useSnackbar();

    const [owners, setOwners] = useState<DealOwner[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);
	const [invalid, setInvalid] = useState<boolean>(false);
	const [mode, setMode] = useState<string>('');
	const [blocked, setBlocked] = useState<boolean>(false);
	const [createMode, setCreateMode] = useState<boolean>(false);
	const [saveMode, setSaveMode] = useState<boolean>(false);
	const [searchMode, setSearchMode] = useState<boolean>(false);
    const [message, setMessage] = useState<string|null>(null);
    const [isNotificationVisible, setIsNotificationVisible] = useState<boolean>(false);
    const [dashboardTitle, setDashboardTitle] = useState<string>('');
    const [selectedDashboard, setSelectedDashboard] = useState<Dashboard|null>(null);

    const localDashboards = useMemo<Dashboard[]>(() => dashboards ?? globalDashboards, [dashboards, globalDashboards]);

    const existing = useMemo(() =>
        (!createMode || !success) && !!localDashboards.find(dashboard => dashboard.title.trim().toLowerCase() === dashboardTitle.trim().toLowerCase())
    ,[createMode, dashboardTitle, localDashboards, success]);

    const companyWebsites = useMemo(() =>
        localDashboards.reduce((map: Map<string, string>, dashboard: Dashboard) => {
            map.set(dashboard.title, (mappedOverviewQueries.get(`${dashboard.id}:Website`) || '').replace('https://', '').replace('http://', ''));
            return map;
        }, new Map<string, string>())
    , [localDashboards, mappedOverviewQueries]);

    const getPlaceHolder = useCallback(() => {
        let placeholder = 'Enter keywords...';

        if (search) {
            if (create && !blocked) {
                placeholder = 'Search or add company...';
            } else
                placeholder = 'Search deals...';
        } else if (create) {
            if (!blocked)
                placeholder = 'Company name...';
            else
                placeholder = 'Limit reached...';
        }

        return placeholder;
    }, [create, blocked, search]);

    const debounced = useDebouncedCallback((value) => {
        const searchValue = value.trim();
        const inputElement = document.createElement('input');

        inputElement.type = 'url';
        inputElement.value = searchValue;

		const invalid = inputElement.value.includes(':/');
        setDashboardTitle(searchValue);
        setInvalid(invalid);
        setSuccess(false);
        setFailed(false);
    }, 500, { maxWait: 2000 });

    const createNewDashboard = useCallback(async () => {
        setLoading(true);
        const lookUpOwner = owners.find(own =>
            own.email?.toLowerCase() === user.attributes.email?.trim()?.toLowerCase());
        const newDashboardDetails: Dashboard = {
            createdAt: (new Date()).toISOString(),
            updatedAt: (new Date()).toISOString(),
            userId: getUserEmail(user!),
            email: getUserEmail(user!),
            group: userGroup!,
            project: userGroup!,
            id: uuidv4(),
            title: dashboardTitle.trim(),
            documents: [],
            selections: [],
            screenshots: [],
            notes: [],
            lastUpdatedBy: "",
            shouldRefresh: false,
            shouldSyncExternally: externalSync,
            refreshData: null,
            summary: "",
            status: defaultStatus || 'new',
            investmentStage: null,
            dealOwner: {
                email: lookUpOwner?.email,
                name: lookUpOwner?.name,
            }
        };

        const isSuccess: boolean = await createDashboardFunc(newDashboardDetails);

        if (isSuccess) {
            setMode('create');
            setSuccess(true);
            setSelectedDashboard(newDashboardDetails);
        } else {
            setFailed(true);
            enqueueSnackbar(`Dashboard already exists.`, {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right"
                },
                autoHideDuration: 4000
            });
        }

        setLoading(false);
        setIsNotificationVisible(true);
    // eslint-disable-next-line
    }, [user, userGroup, dashboardTitle, externalSync, defaultStatus]);

    const handleCreate = useCallback(async () => {
        if (dashboardTitle && !blocked) {
            const inputElement = document.createElement('input');

            inputElement.type = 'url';
            inputElement.value = dashboardTitle.trim();
            if (!inputElement.checkValidity()) {
                const proceed = await onPreExecute?.();

                if (proceed ?? true) {
                    setInvalid(false);
                    await createNewDashboard();
                } else {
                    onElseExecute?.();
                }
            } else {
                setInvalid(true);
            }
        }
    // eslint-disable-next-line
    }, [dashboardTitle, blocked, onPreExecute, onElseExecute]);

    const handleSave = useCallback(async () => {
        if (dashboardTitle && !blocked) {
            const inputElement = document.createElement('input');

            inputElement.type = 'url';
            inputElement.value = dashboardTitle.trim();
            if (!inputElement.checkValidity()) {
                const lookUpDashboard = localDashboards.find(dashboard => dashboard.title.trim().toLowerCase() === dashboardTitle.trim().toLowerCase());
                const proceed = await onPreExecute?.();

                if (proceed ?? true) {
                    if (!!lookUpDashboard) {
                        setMode('save');
                        setSuccess(true);
                        setSelectedDashboard(lookUpDashboard);
                        setInvalid(false);
                    } else {
                        setFailed(true);
                        setInvalid(true);
                    }
                    setIsNotificationVisible(true);
                } else {
                    onElseExecute?.();
                }
            } else {
                setFailed(true);
                setInvalid(true);
                setIsNotificationVisible(true);
            }
        }
    }, [dashboardTitle, blocked, localDashboards, onPreExecute, onElseExecute]);

    const handleReset = useCallback(() => {
        setDashboardTitle('');
        setInvalid(false);
        setSuccess(false);
        setFailed(false);
        setMode('');
        setCreateMode(false);
        setSaveMode(false);
        setMessage('');
        setDashboardTitle('');
        setSelectedDashboard(null);
    }, []);

    useEffect(()=>{
        if (userGroup) {
            getAllUsersFunc(userGroup).then(users => setOwners(users.map((user: any) => ({
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
            }) as DealOwner)));
        }
    }, [userGroup]);

    useEffect(() => {
        if (!isNaN(flags?.monthlyDashboardLimit) && !!subscription && !!usage)
            setBlocked((flags?.monthlyDashboardLimit > 0) && ((usage?.dashboardsCreated ?? 0) >= flags?.monthlyDashboardLimit));
    }, [flags, subscription, usage]);

    useEffect(() => {
        if (!!dashboardTitle)
            setCreateMode(!!create && !existing);
        else
            setCreateMode(false);
    }, [create, existing, dashboardTitle]);

    useEffect(() => {
        if (save && !!dashboardTitle)
            setSaveMode(existing);
        else
            setSaveMode(false);
    }, [dashboardTitle, save, existing]);

    useEffect(() => {
        if (search && !!dashboardTitle)
            setSearchMode(clear || false);
        else
            setSearchMode(false);
    }, [dashboardTitle, save, existing, search, clear]);

    useEffect(() => {
        onSearch?.(dashboardTitle,
			  invalid ? 'invalid'
			: failed ? 'failed'
			: success ? 'success'
			: createMode ? 'create'
			: saveMode ? 'save'
			: blocked ? 'blocked'
            : existing ? 'existing'
			: 'not-found');
    }, [dashboardTitle, invalid, failed, success, createMode, saveMode, blocked, existing, onSearch]);

    useEffect(() => {
		if (execute !== undefined) {
            if (createMode && ['mode', 'create'].includes(execute))
                handleCreate();
            else if (saveMode && ['mode', 'save'].includes(execute))
                handleSave();
            else if (execute === 'reset')
                handleReset();
		}
    // eslint-disable-next-line
    }, [createMode, saveMode, execute]);

    useEffect(() => {
      if (isNotificationVisible) {
        const timeoutId = setTimeout(() => {
            clearTimeout(timeoutId);
            setIsNotificationVisible(false);
            if (!!selectedDashboard) {
                if (mode === 'save')
                    onSave?.(selectedDashboard);
                else if (mode === 'create')
                    onCreate?.(selectedDashboard);
            }
            setMode('');
        }, 3000);

        return () => {
            clearTimeout(timeoutId);
        }
      }
      // eslint-disable-next-line
    }, [isNotificationVisible, selectedDashboard]);

    return (<>
        <Tooltip open={invalid || (notify && existing) || !!message} placement="bottom" title={
				invalid ? (<InvalidNameNotification />) :
                existing ? (<ExistingNameNotification />) :
				(!!message) && (<ErrorNotification message={message} />)
            }
            componentsProps={{tooltip:{sx:{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
                background: (!!message) ? 'red' : 'gray',
                maxWidth: 400,
                width: 'max-content',
                maxHeight: 64,
                height: 'fit-content',
                padding: existing ? '6px 20px' : 2,
                textAlign: 'center',
                opacity: existing ? '0.8 !important' : '1.0',
                "&::before": {
                    position: 'absolute',
                    height: 0,
                    width: 0,
                    top: '5%',
                    left: '50%',
                    borderRight: '12px solid transparent',
                    borderBottom: '12px solid gray',
                    borderLeft: '12px solid transparent',
                    content: '""',
                }
            }}}}>
                <Autocomplete
                    disableClearable
                    openOnFocus
                    autoHighlight
                    autoComplete
                    includeInputInList
                    disablePortal
                    freeSolo
                    forcePopupIcon={false}
                    // open={!autoComplete ? false : undefined}
                    disabled={success || disabled}
                    value={dashboardTitle}
                    onChange={(_, option: string) => debounced(option)}
                    options={ArrayUtils.sortByAscending([...localDashboards], 'title').map((dashboard: Dashboard) => dashboard.title)}
                    filterOptions={(options, { inputValue }) => options.filter(opt => inputValue ? opt?.toLowerCase().includes(inputValue.toLowerCase()) : !!opt)}
                    getOptionLabel={(option: string) => option}
                    PopperComponent={(props) => (<Popper {...props} disablePortal placement={placement as PopperPlacementType}/>)}
                    PaperComponent={(props) => (<Box {...props} className={classes.optionPaper} />)}
                    ListboxProps={{ className: "no-scrollbar", style: { maxHeight: 230 } }}
                    renderOption={(props: any, option: string) => (
                        <Stack direction="column" {...props} className={classes.optionContainer}
                            alignItems="flex-start" justifyContent="center">
                            <Typography className={classes.optionTitle}>{option}</Typography>
                            <Typography className={classes.optionSubtitle}>{companyWebsites.get(option) || ' '}</Typography>
                        </Stack>
                    )}
                    sx={{ width: '100%', }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            className={classnames(
                                classes.inputField,
                                disabled && classes.disabledInputField,
                                smallFit && classes.smallInputField,
                                mediumFit && classes.mediumInputField,
                                squareFit && classes.squareInputField,
                                // mediumSquareFit && classes.mediumSquareInputField,
                            )}
                            placeholder={getPlaceHolder()}
                            disabled={loading || success || disabled}
                            onChange={(e) => debounced(e.target.value)}
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter') && (createMode || !search))
                                    handleCreate();
                            }}
                            sx={{ width: '100%' }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ color: '#0d9488' }}>
                                        {(createMode || !search) ? (<AddRoundedIcon />) : search && (
                                            <SearchIcon height={20} width={20} stroke={'#0d9488'} />
                                        )}
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end" >
										{(execute === undefined) && (<>
											{createMode && (
												<LoadingButton
													variant="contained"
													className={classnames(
														classes.createSaveButton,
														smallFit && classes.smallButton,
														blocked && classes.blocked,
														success && classes.success,
														failed && classes.failed,
													)}
													loading={loading}
													disabled={!dashboardTitle.trim() || success || failed || disabled}
													onClick={handleCreate}>
													{success ? (
														<CheckCircleOutlineIcon/>
													) : (failed) ? (
														<ErrorOutlineIcon />
													) : (
														<>{'Add'}</>
													)}
												</LoadingButton>
											)}
											{saveMode && (
												<LoadingButton
													variant="contained"
													className={classnames(
														classes.createSaveButton,
														smallFit && classes.smallButton,
														blocked && classes.blocked,
														success && classes.success,
														failed && classes.failed,
													)}
													loading={loading}
													disabled={!dashboardTitle.trim() || success || failed || disabled}
													onClick={handleSave}>
													{success ? (
														<CheckCircleOutlineIcon/>
													) : (failed) ? (
														<ErrorOutlineIcon />
													) : (
														<>{'Save'}</>
													)}
												</LoadingButton>
											)}
											{searchMode && (
												<LoadingButton
													variant="text"
                                                    className={classes.clear}
                                                    disableRipple
													onClick={handleReset}>
                                                    {'clear'}
												</LoadingButton>
											)}
										</>)}
                                    </InputAdornment>
                                )}}
                        />
                    )}
                />
        </Tooltip>
    </>);
}

export default withLDConsumer()(DashboardsSearchOrCreate);